<template>
  <div class="spl-deadline-list-table">
    <table v-if="!isLoading">
      <thead>
        <tr class="spl-deadline-table__head">
          <th
            v-for="(header, index) in tableFields"
            :key="'dashboardPrecariageHeader' + index"
            @click="toggleSort(header.name)">
            <span
              v-if="findSort(sortedFields, header.name)"
              :class="findSort(sortedFields, header.name).direction"
              class="order-arrow" />
            {{ header.label }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(deadline, deadlineIndex) in deadlineList" :key="'deadlineTable' + deadlineIndex">
          <td
            v-for="(field, fieldIndex) in tableFields"
            :key="'dashboardPrecariageField' + deadlineIndex + '.' + fieldIndex"
            :class="{'spl-deadline-table__field--number': isNumber(deadline[field.name]),
                     'spl-deadline-table__field--long': field.long,
                     'spl-deadline-table__field--bold': field.bold}"
            @click="deadlineSelected(deadline)">
            <span>
              {{ formatField(deadline[field.name], field.name, field.type) }}
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-if="isLoading" class="spl-load-img" />
  </div>
</template>

<script lang="ts">
import { mapActions, mapGetters } from 'vuex'
import { find, findIndex, orderBy, isNumber } from 'lodash'
import { DeadlineListItem } from '@/webApi/DataModels'
import constants from '@/constants'
import Helpers from '@/helpers.vue'
export default {
  mixins: [Helpers],
  data () {
    return {
      tableFields: [
        { name: 'Customer_Name', label: 'Customer', type: 'String' },
        { name: 'Country_Name', label: 'Country', type: 'String' },
        { name: 'Service_Level_Name', label: 'Service Level', type: 'String' },
        { name: 'From_Postal_Code', label: 'From Zip Code', type: 'String' },
        { name: 'To_Postal_Code', label: 'To Zip Code', type: 'String' },
        { name: 'City', label: 'City', type: 'String' },
        { name: 'Open_Deadline_Day', label: 'Open Deadline Day', type: 'String' },
        { name: 'Open_Deadline_Time', label: 'Open Deadline Time', type: 'String' },
        { name: 'Close_Deadline_Day', label: 'Close Deadline Day', type: 'String' },
        { name: 'Close_Deadline_Time', label: 'Close Deadline Time', type: 'String' },
        { name: 'Valid_From', label: 'Valid From', type: 'Date' },
        { name: 'Valid_To', label: 'Valid To', type: 'Date' },
        { name: 'Weekend_Delivery', label: 'Weekend Del.', type: 'Boolean' },
        { name: 'Delivery', label: 'Delivery Days', type: 'WeekDayDelivery' },
        { name: 'Created_On', label: 'Created On', type: 'Time' },
        { name: 'User_Name', label: 'User', type: 'String' }
      ],
      sortedFields: [],
      isLoading: false
    }
  },
  computed: {
    ...mapGetters('deadline', ['deadlineList']),
    dayNames () {
      return ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']
    }
  },
  methods: {
    ...mapActions('deadline', ['sortList']),
    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField:any = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    },
    deadlineSelected (deadline:DeadlineListItem) {
      this.$router.push('deadline-edit/'+ deadline.Deadline_ID)
    },
    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        sortFields = [constants.FILTER_NAMES.SERVICE_LEVEL]
        sortDirections = ['asc']
      }
      // Use the lodash orderBy method for sorting
      this.sortList({ sortFields, sortDirections })
    },
    // Special formatting for fields
    formatField (field, name, type) {
      if (type === 'Date') {
        return this.formatDate(field, false, 'YYYY-MM-DD')
      } else if (type === 'Time') {
        return this.formatDate(field, true, 'YYYY-MM-DD HH:mm')
      } else if (type === 'Boolean') {
        return field ? 'Yes' : 'No'
      } else if (type === 'WeekDayDelivery') {
        return field.split('').map((value, index) => {
          if (value === '1') {
            return this.dayNames[index]
          } else {
            return ''
          }
        }).filter(day => day !== '').join(', ')
      }
      // empty fields will be removed
      if (field == null || field === 0) {
        return ''
      }
      return field
    },
    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },
    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    }
  }

}
</script>

<style lang="scss">
  @import '../../../variables.scss';
  .spl-deadline-list-table {
    margin: 0 70px;
    width: calc(100% - 138px);

    table {
      border-spacing: 0;
      table-layout: fixed;
      padding-bottom: 20px;
    }
    &__head {
      th {
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 5px 0 rgba(0, 0, 0, 0.1);
      }
      &--sticky {
        position: sticky;
        left: 41px;
        z-index: 3 !important;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 10px 2px 0 6px;
        }
        position: sticky;
        left: 0px;
        z-index: 3 !important;
      }
    }
    &__row {
      &--selected {
        background-color: $tableRowBackgroundColorSelected !important;
      }
      &--deleted {
        opacity: 0.3;
        background-color: lightgrey !important;
      }
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--sticky {
        position: sticky;
        left: 41px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 0 2px 0 6px;
        }
        position: sticky;
        background-color: $tableHeaderBackgroundColor;
        left: 0;
      }
      &--long {
        max-width: 400px !important;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      &--success {
        border-bottom: 2px solid $green !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $green;
      }
      &--warning {
        border-bottom: 2px solid $darkYellow !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $darkYellow;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--bold {
        font-weight: 600;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        text-align: left;
        font-family: $mono;
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 12px;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }
}
</style>
